import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import SubPageLayout from "../../components/shared/SubPageLayout";
import {
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
} from "@mui/icons-material";

import logger from '../../utils/logger';

// Mock data - replace with actual API calls later
const MOCK_MASTER_ACCOUNTS = [
  { id: "MA001", name: "Global Investment Trust", feeAccount: "1234-5678" },
  { id: "MA002", name: "Retirement Portfolio Master", feeAccount: "8765-4321" },
  { id: "MA003", name: "High Growth Fund", feeAccount: "2468-1357" },
];

const MOCK_FEE_NOTES = {
  MA001: { note: "Standard monthly fee structure applied", status: "Approved" },
  MA002: {
    note: "Fee waived for promotional period",
    status: "Pending Review",
  },
  MA003: { note: "Custom fee structure in place", status: "Approved" },
};

const steps = [
  "Select Master Accounts",
  "Review Fee Notes",
  "Billing Worksheet",
  "Summary",
];

const Billing = () => {
  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [selectedMasters, setSelectedMasters] = useState([]);
  const [masterAccount, setMasterAccount] = useState("");
  const [associatedFeeAccount, setAssociatedFeeAccount] = useState("****-****");
  const [totalFees, setTotalFees] = useState(0);
  const [rows, setRows] = useState([]);
  const [viewMode, setViewMode] = useState("Entire Worksheet");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedDraft, setSavedDraft] = useState(null);

  // File upload handling
  const onDrop = (acceptedFiles) => {
    logger.info("Files dropped:", acceptedFiles);
    // TODO: Implement CSV/TXT/MFA file processing
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "text/plain": [".txt"],
      "application/mfa": [".mfa"],
    },
  });

  // Navigation handlers
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedMasters([]);
    setRows([]);
    setTotalFees(0);
  };

  // Step 1: Master Account Selection
  const MasterAccountSelection = () => (
    <Card className="mt-4">
      <CardContent>
        <Typography variant="h6" className="mb-4">
          Select Master Accounts
        </Typography>
        <div className="space-y-4">
          {MOCK_MASTER_ACCOUNTS.map((account) => (
            <div
              key={account.id}
              className="flex items-center justify-between p-3 border rounded hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <div>
                <Typography variant="subtitle1">{account.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Fee Account: {account.feeAccount}
                </Typography>
              </div>
              <Button
                variant={
                  selectedMasters.includes(account.id)
                    ? "contained"
                    : "outlined"
                }
                onClick={() => {
                  setSelectedMasters((prev) =>
                    prev.includes(account.id)
                      ? prev.filter((id) => id !== account.id)
                      : [...prev, account.id],
                  );
                }}
              >
                {selectedMasters.includes(account.id) ? "Selected" : "Select"}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  // Step 2: Fee Notes Review
  const FeeNotesReview = () => (
    <Card className="mt-4">
      <CardContent>
        <Typography variant="h6" className="mb-4">
          Review Fee Notes
        </Typography>
        <div className="space-y-4">
          {selectedMasters.map((masterId) => {
            const account = MOCK_MASTER_ACCOUNTS.find(
              (acc) => acc.id === masterId,
            );
            const feeNote = MOCK_FEE_NOTES[masterId];
            return (
              <div key={masterId} className="p-4 border rounded">
                <div className="flex justify-between items-start">
                  <div>
                    <Typography variant="subtitle1">{account.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {feeNote.note}
                    </Typography>
                  </div>
                  <Chip
                    label={feeNote.status}
                    color={
                      feeNote.status === "Approved" ? "success" : "warning"
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );

  // Step 3: Billing Worksheet
  const BillingWorksheet = () => (
    <Card className="mt-4">
      <CardContent>
        <div className="flex justify-between items-center mb-4">
          <Typography variant="h6">Billing Worksheet</Typography>
          <div className="space-x-2">
            <Button
              startIcon={<SaveIcon />}
              variant="outlined"
              onClick={() => setSavedDraft(rows)}
            >
              Save Draft
            </Button>
            <Button
              startIcon={<CloudUploadIcon />}
              variant="outlined"
              {...getRootProps()}
            >
              Import File
              <input {...getInputProps()} />
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider w-16">
                  #
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Account
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Fee Amount
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Account Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
              {[...Array(5)].map((_, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-2 whitespace-nowrap text-sm">
                    {index + 1}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <TextField size="small" fullWidth />
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      InputProps={{
                        startAdornment: "$",
                      }}
                    />
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <TextField size="small" fullWidth />
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <Chip label="Pending" color="warning" size="small" />
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <IconButton size="small">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex justify-between items-center">
          <Typography variant="subtitle1">
            Total Fees: ${totalFees.toFixed(2)}
          </Typography>
          <div className="space-x-2">
            <Button startIcon={<PrintIcon />} variant="outlined">
              Print
            </Button>
            <Button startIcon={<DownloadIcon />} variant="outlined">
              Export
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Step 4: Summary
  const Summary = () => (
    <Card className="mt-4">
      <CardContent>
        <Typography variant="h6" className="mb-4">
          Billing Summary
        </Typography>
        <div className="space-y-4">
          <Alert severity="success">
            Billing process completed successfully!
          </Alert>
          <div className="p-4 border rounded">
            <Typography variant="subtitle1" className="mb-2">
              Process Summary
            </Typography>
            <div className="space-y-2">
              <div className="flex justify-between">
                <Typography>Total Master Accounts:</Typography>
                <Typography>{selectedMasters.length}</Typography>
              </div>
              <div className="flex justify-between">
                <Typography>Total Fee Amount:</Typography>
                <Typography>${totalFees.toFixed(2)}</Typography>
              </div>
              <div className="flex justify-between">
                <Typography>Process Date:</Typography>
                <Typography>{new Date().toLocaleDateString()}</Typography>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button startIcon={<PrintIcon />} variant="outlined">
              Print Summary
            </Button>
            <Button startIcon={<DownloadIcon />} variant="outlined">
              Download Report
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Render step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <MasterAccountSelection />;
      case 1:
        return <FeeNotesReview />;
      case 2:
        return <BillingWorksheet />;
      case 3:
        return <Summary />;
      default:
        return "Unknown step";
    }
  };

  const breadcrumbItems = [
    { label: "Portfolio Management", link: "/portfolio-management" },
    { label: "Billing", link: "/portfolio-management/billing" },
  ];

  return (
    <SubPageLayout
      title="Management Fees"
      subtitle="Process and manage client management fees"
      breadcrumbItems={breadcrumbItems}
    >
      <Paper className="p-6">
        <Stepper activeStep={activeStep} className="mb-8">
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === steps.length ? (
          <div className="text-center">
            <Typography className="mb-4">
              All steps completed - you're finished
            </Typography>
            <Button onClick={handleReset} variant="contained">
              Start New Billing Process
            </Button>
          </div>
        ) : (
          <>
            {getStepContent(activeStep)}
            <div className="mt-8 flex justify-between">
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
              <div className="space-x-2">
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setIsSubmitting(true);
                      // Simulate API call
                      setTimeout(() => {
                        setIsSubmitting(false);
                        handleNext();
                      }, 1500);
                    }}
                    disabled={isSubmitting}
                    startIcon={<SendIcon />}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Billing"}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForwardIcon />}
                    disabled={activeStep === 0 && selectedMasters.length === 0}
                  >
                    Next
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </Paper>
    </SubPageLayout>
  );
};

export default Billing;
